import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Shield, CheckCircle, Clock, Users, AlertTriangle, RefreshCw,
  Target, Zap, Flag, Eye, ArrowRight, BarChart3, AlertCircle
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATUS_COLORS = {
  posted: { bg: "rgba(0,92,238,0.06)", border: "rgba(0,92,238,0.12)", text: "#005CEE" },
  parsed: { bg: "rgba(138,79,255,0.06)", border: "rgba(138,79,255,0.12)", text: "#8A4FFF" },
  open_to_claim: { bg: "rgba(0,92,238,0.06)", border: "rgba(0,92,238,0.12)", text: "#005CEE" },
  accepted: { bg: "rgba(138,79,255,0.06)", border: "rgba(138,79,255,0.12)", text: "#8A4FFF" },
  execution_started: { bg: "rgba(0,92,238,0.06)", border: "rgba(0,92,238,0.12)", text: "#005CEE" },
  draft_ready: { bg: "rgba(138,79,255,0.06)", border: "rgba(138,79,255,0.12)", text: "#8A4FFF" },
  review_in_progress: { bg: "rgba(138,79,255,0.06)", border: "rgba(138,79,255,0.12)", text: "#8A4FFF" },
  revision_requested: { bg: "rgba(255,51,102,0.06)", border: "rgba(255,51,102,0.12)", text: "#FF3366" },
  completed: { bg: "rgba(0,92,238,0.06)", border: "rgba(0,92,238,0.12)", text: "#005CEE" },
  dispute: { bg: "rgba(255,51,102,0.06)", border: "rgba(255,51,102,0.12)", text: "#FF3366" },
};

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [disputeReason, setDisputeReason] = useState("");
  const [disputeTaskId, setDisputeTaskId] = useState(null);

  useEffect(() => {
    if (user === null) { navigate("/login"); return; }
    if (user === undefined) return;
    if (user.role !== "admin") { navigate("/"); return; }
    loadData();
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, tasksRes] = await Promise.all([
        api.get("/api/workspace/supervision-stats"),
        api.get("/api/workspace/all-tasks"),
      ]);
      setStats(statsRes.data);
      setTasks(tasksRes.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadFilteredTasks = async (f) => {
    setFilter(f);
    try {
      const params = {};
      if (f === "disputes") params.status = "dispute";
      else if (f === "flagged") params.flagged = true;
      else if (f === "open") params.status = "open_to_claim";
      else if (f === "in_progress") params.status = "execution_started";
      else if (f === "review") params.status = "review_in_progress";
      else if (f === "revision") params.status = "revision_requested";

      const res = await api.get("/api/workspace/all-tasks", { params });
      setTasks(res.data || []);
    } catch (e) { console.error(e); }
  };

  const handleDispute = async (taskId) => {
    try {
      await api.post(`/api/workspace/tasks/${taskId}/dispute`, { reason: disputeReason });
      setDisputeTaskId(null);
      setDisputeReason("");
      loadData();
    } catch (e) { console.error(e); }
  };

  const handleResolve = async (taskId) => {
    try {
      await api.post(`/api/workspace/tasks/${taskId}/resolve-dispute`);
      loadData();
    } catch (e) { console.error(e); }
  };

  if (user === undefined || loading) return (
    <div style={{ paddingTop: 100, textAlign: "center" }}>
      <div style={{ width: 40, height: 40, border: "3px solid rgba(59,130,246,0.1)", borderTop: "3px solid var(--accent)", borderRadius: "50%", margin: "0 auto", animation: "spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  const STAT_CARDS = stats ? [
    { label: "Total Tasks", value: stats.total_tasks, icon: BarChart3, color: "var(--primary)" },
    { label: "Open to Claim", value: stats.open_to_claim, icon: Target, color: "#005CEE" },
    { label: "In Progress", value: stats.in_progress, icon: Zap, color: "#8A4FFF" },
    { label: "Completed", value: stats.completed, icon: CheckCircle, color: "#005CEE" },
    { label: "Disputes", value: stats.disputes, icon: AlertTriangle, color: "#FF3366" },
    { label: "Flagged", value: stats.flagged, icon: Flag, color: "#FF3366" },
    { label: "Revisions", value: stats.revision_requested, icon: Clock, color: "#8A4FFF" },
  ] : [];

  const FILTERS = [
    { key: "all", label: "All" },
    { key: "disputes", label: "Disputes" },
    { key: "flagged", label: "Flagged" },
    { key: "open", label: "Open" },
    { key: "in_progress", label: "Executing" },
    { key: "review", label: "In Review" },
    { key: "revision", label: "Revisions" },
  ];

  return (
    <div style={{ paddingTop: 80, minHeight: "100vh", background: "var(--bg-base)" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "2rem 24px 64px" }}>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>

          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2rem" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "0.3rem" }}>
                <Shield size={18} color="var(--accent)" />
                <h1 data-testid="admin-title" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "1.4rem", letterSpacing: "-0.02em" }}>Supervision Dashboard</h1>
              </div>
              <p style={{ color: "var(--text-muted)", fontSize: "0.83rem", fontFamily: "Satoshi, sans-serif" }}>
                Monitor task flow, resolve disputes, flag quality issues
              </p>
            </div>
            <button data-testid="admin-refresh" className="btn-secondary" onClick={loadData} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <RefreshCw size={13} /> Refresh
            </button>
          </div>

          {/* Stats Grid */}
          {stats && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 12, marginBottom: "2rem" }}>
              {STAT_CARDS.map(({ label, value, icon: Icon, color }) => (
                <div key={label} data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`} className="glass-card" style={{ padding: "1.1rem" }}>
                  <Icon size={16} color={color} style={{ marginBottom: 6 }} />
                  <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "1.5rem", color: "var(--text-main)" }}>{value}</div>
                  <div style={{ fontSize: "0.68rem", color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif" }}>{label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Filter tabs */}
          <div style={{ display: "flex", gap: 6, marginBottom: "1.5rem", flexWrap: "wrap" }}>
            {FILTERS.map(f => (
              <button key={f.key} data-testid={`filter-${f.key}`} onClick={() => f.key === "all" ? loadData().then(() => setFilter("all")) : loadFilteredTasks(f.key)}
                style={{
                  padding: "0.4rem 1rem", borderRadius: 8, fontSize: "0.78rem", fontWeight: filter === f.key ? 600 : 400,
                  color: filter === f.key ? "var(--accent-text)" : "var(--text-muted)",
                  background: filter === f.key ? "var(--accent-subtle)" : "transparent",
                  border: filter === f.key ? "1px solid rgba(59,130,246,0.1)" : "1px solid transparent",
                  cursor: "pointer", fontFamily: "'Manrope', sans-serif",
                }}>
                {f.label}
              </button>
            ))}
          </div>

          {/* Task List */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "1rem" }}>
            <div className="trust-bar" />
            <h2 style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "1rem" }}>
              Tasks ({tasks.length})
            </h2>
          </div>

          {tasks.length === 0 ? (
            <div className="glass-card" data-testid="no-tasks" style={{ padding: "2.5rem", textAlign: "center" }}>
              <CheckCircle size={32} color="var(--success)" style={{ margin: "0 auto 1rem" }} />
              <p style={{ color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif" }}>No tasks match this filter.</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {tasks.map((task, i) => {
                const sc = STATUS_COLORS[task.status] || STATUS_COLORS.posted;
                return (
                  <motion.div key={task.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                    <div data-testid={`admin-task-${i}`} className="glass-card" style={{ padding: "1.2rem 1.4rem" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                            <h3 style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "0.92rem", color: "var(--text-main)" }}>
                              {task.interpreted_goal || task.original_query}
                            </h3>
                            {task.flagged && (
                              <Flag size={12} color="var(--pink)" fill="var(--pink)" />
                            )}
                          </div>
                          <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif", lineHeight: 1.5, marginBottom: 6 }}>
                            {task.original_query?.slice(0, 120)}
                          </p>
                          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                            <span style={{
                              padding: "2px 10px", borderRadius: 100, fontSize: "0.68rem", fontWeight: 600,
                              fontFamily: "'Manrope', sans-serif",
                              color: sc.text, background: sc.bg, border: `1px solid ${sc.border}`,
                            }}>
                              {task.status?.replace(/_/g, " ")}
                            </span>
                            <span style={{ fontSize: "0.72rem", color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif" }}>
                              {task.category?.replace(/_/g, " ")}
                            </span>
                            <span style={{ fontSize: "0.72rem", color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif" }}>
                              ${task.estimated_payout?.toLocaleString() || "—"}
                            </span>
                            {task.assigned_doer_name && (
                              <span style={{ fontSize: "0.72rem", color: "var(--accent)", fontFamily: "'Satoshi', sans-serif", display: "flex", alignItems: "center", gap: 3 }}>
                                <Users size={10} /> {task.assigned_doer_name}
                              </span>
                            )}
                            <span style={{ fontSize: "0.68rem", color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif" }}>
                              by {task.poster_name}
                            </span>
                          </div>
                          {task.flag_reason && (
                            <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 4 }}>
                              <AlertCircle size={11} color="var(--pink)" />
                              <span style={{ fontSize: "0.72rem", color: "var(--pink)", fontFamily: "'Satoshi', sans-serif" }}>
                                {task.flag_reason}
                              </span>
                            </div>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap" }}>
                          {task.status === "dispute" ? (
                            <button data-testid={`resolve-btn-${i}`} onClick={() => handleResolve(task.id)}
                              style={{ padding: "0.35rem 0.8rem", borderRadius: 8, background: "rgba(0,92,238,0.06)", border: "1px solid rgba(0,92,238,0.15)", color: "var(--primary)", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer", fontFamily: "'Manrope', sans-serif" }}>
                              Resolve
                            </button>
                          ) : task.status !== "completed" ? (
                            <>
                              {disputeTaskId === task.id ? (
                                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                                  <input value={disputeReason} onChange={e => setDisputeReason(e.target.value)}
                                    placeholder="Reason..." style={{ padding: "0.3rem 0.6rem", borderRadius: 6, border: "1px solid var(--bg-neutral)", fontSize: "0.72rem", width: 140, fontFamily: "'Satoshi', sans-serif" }} />
                                  <button onClick={() => handleDispute(task.id)}
                                    style={{ padding: "0.3rem 0.6rem", borderRadius: 6, background: "rgba(255,51,102,0.06)", border: "1px solid rgba(255,51,102,0.15)", color: "var(--pink)", fontSize: "0.72rem", fontWeight: 600, cursor: "pointer" }}>
                                    Flag
                                  </button>
                                  <button onClick={() => { setDisputeTaskId(null); setDisputeReason(""); }}
                                    style={{ padding: "0.3rem 0.5rem", borderRadius: 6, background: "transparent", border: "1px solid var(--bg-neutral)", color: "var(--text-muted)", fontSize: "0.72rem", cursor: "pointer" }}>
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <button data-testid={`dispute-btn-${i}`} onClick={() => setDisputeTaskId(task.id)}
                                  style={{ padding: "0.35rem 0.8rem", borderRadius: 8, background: "rgba(255,51,102,0.06)", border: "1px solid rgba(255,51,102,0.12)", color: "var(--pink)", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer", fontFamily: "'Manrope', sans-serif", display: "flex", alignItems: "center", gap: 4 }}>
                                  <AlertTriangle size={11} /> Dispute
                                </button>
                              )}
                              <button data-testid={`view-task-${i}`} onClick={() => navigate(`/workspace/${task.id}`)}
                                style={{ padding: "0.35rem 0.8rem", borderRadius: 8, background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.15)", color: "var(--accent)", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer", fontFamily: "'Manrope', sans-serif", display: "flex", alignItems: "center", gap: 4 }}>
                                <Eye size={11} /> View
                              </button>
                            </>
                          ) : (
                            <span style={{ fontSize: "0.75rem", color: "var(--success)", fontWeight: 600, fontFamily: "'Manrope', sans-serif", display: "flex", alignItems: "center", gap: 4 }}>
                              <CheckCircle size={12} /> Done
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </motion.div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
